/**
 * Same-pod review child supervisor. When the API parks a review session on
 * this (builder) pod and pushes `session:spawnReview`, the supervisor spawns a
 * SECOND conveyor-agent process — same CLI binary, same repo checkout — bound
 * to the review session via env overrides. The child owns its own
 * AgentConnection/PTY stream, so the card's "Reviewer" terminal tab appears
 * through the normal multi-session relay with no relay changes.
 *
 * Single-slot: a new spawn (re-review of a fresh push) kills and replaces the
 * previous child. Spawn failures are reported back to the API
 * (`reportReviewSpawnFailure`), which Ends the orphaned session and falls back
 * to a dedicated review pod.
 *
 * Shared-checkout caveat (v1, deliberate): the reviewer reads the builder's
 * working tree. At review time the builder is parked (ReviewPR + CI green),
 * so contention is low; a best-effort ff-only fetch freshens the branch. A
 * human typing in the builder TUI mid-review can still dirty the tree —
 * worktree isolation is a deferred follow-up.
 */
import { spawn as nodeSpawn, execFile, type ChildProcess } from "node:child_process";
import { promisify } from "node:util";
import type { SpawnReviewData } from "../connection/agent-connection.js";
import { createServiceLogger } from "../utils/logger.js";

const execFileAsync = promisify(execFile);
const logger = createServiceLogger("ReviewChild");

/** Exit inside this window (non-zero) is treated as a spawn failure. */
export const SPAWN_FAILURE_GRACE_MS = 15_000;
/** How long to wait for a SIGTERM'd child before SIGKILL. */
export const KILL_WAIT_MS = 5_000;

export interface ReviewChildConnection {
  reportReviewSpawnFailure(reviewSessionId: string, error?: string): void;
}

export type SpawnFn = (
  command: string,
  args: string[],
  options: {
    cwd: string;
    env: NodeJS.ProcessEnv;
    stdio: ["ignore", "pipe", "pipe"];
  },
) => ChildProcess;

/**
 * Build the child env from the builder's env. The child is the same CLI in
 * `code-review` mode, so it reuses the pod's credentials (git, Anthropic)
 * while swapping in the review session's identity.
 */
export function buildReviewChildEnv(
  baseEnv: NodeJS.ProcessEnv,
  data: SpawnReviewData,
): NodeJS.ProcessEnv {
  const env: NodeJS.ProcessEnv = { ...baseEnv };
  // The review session's identity: its own JWT + sessionId, review runner
  // mode. CONVEYOR_TASK_ID stays the parent's — same task (SEC-9 derives the
  // task from the JWT server-side).
  env.CONVEYOR_TASK_TOKEN = data.sessionJwt;
  env.CONVEYOR_SESSION_ID = data.sessionId;
  env.CONVEYOR_MODE = "code-review";

  // The bootstrap-refresh path would replace the child's review JWT with the
  // BUILDER's writer JWT (findBundleSession prefers the live writer), silently
  // re-identifying the child as the builder. Reviews finish well inside the
  // 24h JWT TTL, so the child never needs a refresh — remove the tokens.
  delete env.POD_BOOTSTRAP_TOKEN;
  delete env.CONVEYOR_BOOTSTRAP_TOKEN;

  // The builder already ran (or is running) setup/start on this shared
  // workspace — the child must never re-run `bun install` or spawn a second
  // dev server.
  delete env.CONVEYOR_SETUP_COMMAND;
  delete env.CONVEYOR_START_COMMAND;

  // The code-review runner forces its own mode (review + auto); a stale
  // builder agent-mode hint must not leak in.
  delete env.CONVEYOR_AGENT_MODE;
  delete env.CONVEYOR_IS_AUTO;
  return env;
}

export class ReviewChildSupervisor {
  private child: ChildProcess | null = null;
  private childSessionId: string | null = null;

  constructor(
    private readonly connection: ReviewChildConnection,
    private readonly workspaceDir: string,
    private readonly spawnFn: SpawnFn = nodeSpawn as SpawnFn,
  ) {}

  /** Spawn (or replace) the review child for a `session:spawnReview` push. */
  async spawn(data: SpawnReviewData): Promise<void> {
    logger.info("Spawning same-pod review child", {
      reviewSessionId: data.sessionId,
      branch: data.branch ?? undefined,
      prNumber: data.prNumber ?? undefined,
    });
    await this.stopCurrent("superseded by new review spawn");
    await this.refreshCheckout(data.branch ?? null);

    const env = buildReviewChildEnv(process.env, data);
    const cliPath = process.argv[1];
    if (!cliPath) {
      this.connection.reportReviewSpawnFailure(data.sessionId, "cannot resolve CLI entry path");
      return;
    }

    let child: ChildProcess;
    try {
      child = this.spawnFn(process.execPath, [cliPath], {
        cwd: this.workspaceDir,
        env,
        stdio: ["ignore", "pipe", "pipe"],
      });
    } catch (err) {
      this.connection.reportReviewSpawnFailure(
        data.sessionId,
        err instanceof Error ? err.message : String(err),
      );
      return;
    }

    this.child = child;
    this.childSessionId = data.sessionId;
    const spawnedAt = Date.now();
    let reported = false;
    const reportOnce = (message: string): void => {
      if (reported) return;
      reported = true;
      this.connection.reportReviewSpawnFailure(data.sessionId, message);
    };

    // Child output lands in the pod log (the entrypoint tees our stdio),
    // prefixed so builder and reviewer streams stay distinguishable.
    child.stdout?.on("data", (chunk: Buffer) => {
      process.stdout.write(`[review-child] ${chunk.toString()}`);
    });
    child.stderr?.on("data", (chunk: Buffer) => {
      process.stderr.write(`[review-child] ${chunk.toString()}`);
    });

    child.on("error", (err) => {
      logger.error("Review child spawn error", { error: err.message });
      if (this.child === child) {
        this.child = null;
        this.childSessionId = null;
      }
      reportOnce(err.message);
    });

    child.on("exit", (code, signal) => {
      const wasCurrent = this.child === child;
      if (wasCurrent) {
        this.child = null;
        this.childSessionId = null;
      }
      logger.info("Review child exited", { code, signal, reviewSessionId: data.sessionId });
      // A non-zero exit right after launch means the child never became the
      // review agent — recover via the dedicated-pod fallback. Later exits
      // (review finished, server-sent session:stop, supersede kill) are
      // normal lifecycle: the server owns the session state there.
      const withinGrace = Date.now() - spawnedAt < SPAWN_FAILURE_GRACE_MS;
      if (wasCurrent && withinGrace && code !== null && code !== 0) {
        reportOnce(`review child exited with code ${code} within startup grace`);
      }
    });
  }

  /** Stop any live child (parent shutdown / supersede). Best-effort. */
  async stopAll(): Promise<void> {
    await this.stopCurrent("parent shutting down");
  }

  private async stopCurrent(reason: string): Promise<void> {
    const child = this.child;
    if (!child || child.exitCode !== null || child.killed) {
      this.child = null;
      this.childSessionId = null;
      return;
    }
    logger.info("Stopping review child", { reason, reviewSessionId: this.childSessionId });
    // Detach bookkeeping first so the exit handler treats this as expected.
    this.child = null;
    this.childSessionId = null;

    await new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        try {
          child.kill("SIGKILL");
        } catch {
          /* already gone */
        }
        resolve();
      }, KILL_WAIT_MS);
      timer.unref();
      child.once("exit", () => {
        clearTimeout(timer);
        resolve();
      });
      try {
        child.kill("SIGTERM");
      } catch {
        clearTimeout(timer);
        resolve();
      }
    });
  }

  /**
   * Best-effort branch freshen before the review reads the tree: fetch the PR
   * branch and fast-forward only (never clobber local work — the builder's
   * checkout is the source of truth on conflict). Failures are logged and
   * skipped; the reviewer still reads the diff via the GitHub API.
   */
  private async refreshCheckout(branch: string | null): Promise<void> {
    if (!branch) return;
    try {
      await execFileAsync("git", ["fetch", "origin", branch], {
        cwd: this.workspaceDir,
        timeout: 60_000,
      });
      await execFileAsync("git", ["merge", "--ff-only", `origin/${branch}`], {
        cwd: this.workspaceDir,
        timeout: 30_000,
      });
    } catch (err) {
      logger.warn("Review checkout freshen skipped", {
        branch,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
}
